/**
 * The embed session's non-extractable signing key — SER-5887.
 *
 * Storage half of DPoP; the proof builder is embed-dpop-proof.ts. Kept apart
 * because `indexedDB` is `undefined` under the Bun + happy-dom test runtime
 * while `crypto.subtle` is present, so the builder can be tested directly and
 * only this thin layer needs an injectable seam.
 *
 * WHY INDEXEDDB, and not the two obvious alternatives:
 *
 *   sessionStorage — would require `extractable: true` so the key could be
 *     serialised, and the key would then be copied along with the bearer. That
 *     defeats the entire feature: the attack being closed IS someone copying
 *     sessionStorage.
 *
 *   in-memory only — strongest at rest, but the embed bootstrap reuses a stored
 *     session on reload WITHOUT re-exchanging, and the loader has already marked
 *     its token delivered. A key that died with the page would make every reload
 *     a dead end.
 *
 * IndexedDB persists a `CryptoKey` by structured clone, so the handle survives a
 * reload while the private bytes stay inside the browser's key store. Generated
 * with `extractable: false`, so `exportKey` throws — there is no API, devtools
 * included, that gets the private half out.
 *
 * This does NOT make same-origin XSS harmless: script running inside the real
 * frame can still USE the key to sign. It stops the key being stolen, not used.
 * CSP and frontend hygiene remain necessary.
 */
import { createStore, del, get, set } from 'idb-keyval'
import { DPOP_KEY_ALGORITHM } from './embed-dpop-proof'
import { embedInstanceId } from './embed-session'

/**
 * A DEDICATED store, deliberately not idb-keyval's default.
 *
 * `use-auth.tsx` calls `clear()` from idb-keyval on console logout, which wipes
 * the DEFAULT store for the whole origin. The console and the embed iframe share
 * an origin, so a logout in one tab would silently destroy the signing key of
 * every embed iframe in another — and each of those would then 401 forever with
 * no way back. A named store is outside that blast radius.
 *
 * Created lazily: `createStore` opens an IndexedDB connection at call time, and
 * doing that at module load would throw in any context without IndexedDB (the
 * test runtime, and a browser that blocks third-party storage).
 */
let storeHandle: ReturnType<typeof createStore> | undefined
const defaultStore = () => {
  if (!storeHandle) {
    storeHandle = createStore('embed-dpop', 'keys')
  }
  return storeHandle
}

/**
 * The seam tests inject through, rather than reaching for `mock.module` (which
 * leaks process-globally in bun — AGENTS.md failure mode 1) or adding a
 * fake-indexeddb dependency. Same shape as the `timezone?` parameter pattern in
 * `api-console/applications/date-filter-params.ts`.
 */
export interface DpopKeyStore {
  read(key: string): Promise<CryptoKeyPair | undefined>
  write(key: string, value: CryptoKeyPair): Promise<void>
  remove(key: string): Promise<void>
}

const idbStore: DpopKeyStore = {
  read: (key) => get<CryptoKeyPair>(key, defaultStore()),
  write: (key, value) => set(key, value, defaultStore()),
  remove: (key) => del(key, defaultStore()),
}

/**
 * Namespaced by embed instance, exactly as the session is
 * (`embedStorageKey`). Two iframes in one tab share an origin AND an IndexedDB,
 * so a shared key would let a second instance sign for the first — the same
 * tenant-confusion class already fixed for the session itself.
 */
const keyName = (): string => {
  const instance = embedInstanceId()
  return instance ? `dpop-key:${instance}` : 'dpop-key'
}

/**
 * The keypair for this iframe instance, creating one on first use.
 *
 * Returns undefined when IndexedDB is unavailable — a browser blocking
 * third-party storage, or the test runtime. The caller must treat that as "no
 * proof can be produced" rather than crashing: the embed already surfaces
 * blocked storage through the session-persist check, and DPoP should fail the
 * same way rather than inventing a second failure mode.
 */
export const getOrCreateDpopKeyPair = async (
  store: DpopKeyStore = idbStore,
): Promise<CryptoKeyPair | undefined> => {
  const name = keyName()
  // SINGLE-FLIGHT, because read-then-generate-then-write is not atomic and the
  // bootstrap has more than one caller: the mint thumbprint, the request
  // interceptor and the teardown proof can all ask for the key within the same
  // tick of a cold boot. Two concurrent calls would each read nothing, each
  // generate, and each write — last writer wins, and whichever key was NOT
  // stored may already have been used to bind the session. Every later request
  // then signs with the stored key and 401s forever, with no way back.
  //
  // Keyed by name so two iframe instances in one tab (which namespace their
  // keys) do not share a flight.
  const flight = inFlight.get(name)
  if (flight) {
    return flight
  }
  const started = (async () => {
    try {
      const existing = await store.read(name)
      if (existing?.privateKey && existing?.publicKey) {
        return existing
      }
      // `false` is the whole point — the private half can never be exported.
      // `sign`/`verify` only; this key signs proofs and nothing else.
      const keyPair = (await crypto.subtle.generateKey(DPOP_KEY_ALGORITHM, false, [
        'sign',
        'verify',
      ])) as CryptoKeyPair
      await store.write(name, keyPair)
      // FIRST WRITER WINS: read back and return what is actually STORED, not
      // what we just generated. Single-flight covers this tab, but a second
      // iframe or a duplicated tab shares the IndexedDB and can have written
      // between the read and the write above. Returning the stored value keeps
      // the key that signs and the key whose thumbprint was minted identical —
      // which is the only property that matters here.
      const stored = await store.read(name)
      return stored?.privateKey && stored?.publicKey ? stored : keyPair
    } catch {
      return undefined
    } finally {
      // Cleared on settle, not cached: a failed generation must be retryable,
      // and a successful one is cheap to re-read from IndexedDB.
      inFlight.delete(name)
    }
  })()
  inFlight.set(name, started)
  return started
}

/**
 * In-flight key creations, keyed by storage name. Module-scoped because the
 * callers are independent modules; an instance field would not be shared.
 */
const inFlight = new Map<string, Promise<CryptoKeyPair | undefined>>()

/**
 * Whether a key already exists, without creating one.
 *
 * The bootstrap needs this to spot the one unrecoverable state: a session in
 * sessionStorage that hodor has BOUND to a key, while the key itself is gone
 * (IndexedDB cleared, a different browser profile, storage eviction). Every
 * request would then 401 forever. Seeing it early lets the iframe revoke, clear
 * and ask the host to re-mint instead of spinning.
 */
export const hasDpopKeyPair = async (store: DpopKeyStore = idbStore): Promise<boolean> => {
  try {
    const existing = await store.read(keyName())
    return Boolean(existing?.privateKey)
  } catch {
    return false
  }
}

/** Drops the key, so a fresh session starts from a fresh keypair. */
export const clearDpopKeyPair = async (store: DpopKeyStore = idbStore): Promise<void> => {
  try {
    await store.remove(keyName())
  } catch {
    // Best-effort, mirroring clearEmbedSession: a key we cannot delete is inert
    // once its session is gone.
  }
}
